import { SecurityHelper } from '../helpers.js';
import { SWIPE_THRESHOLD } from '../constants.js';

// Schema:
//   {
//     type: 'property_carousel',
//     items: [{
//       title, price?, surface?, rooms?, location?,
//       images?: string[], url?, features?: string[]
//     }]
//   }
//
// One outer carousel groups all items (one item per slide). Each item is a
// card containing its own mini photo carousel when several images are
// provided. Between-property navigation lives in a control bar BELOW the
// card (page arrows around the dot indicators) so it never overlaps the
// per-photo arrows that sit on the image.
//
// Cards intentionally don't render a `description`: the scraped meta
// descriptions are inconsistent (present for some listings, missing for
// others), which produced visibly uneven cards. The full details live one
// click away behind the "Voir l'annonce" link; the card stays compact and
// uniform across listings.
export function renderPropertyCarousel(block, ctx = {}) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;

  const t = ctx.t || {};
  const prevLabel = t.prevSlide || 'Précédent';
  const nextLabel = t.nextSlide || 'Suivant';
  const sliderLabel = t.propertyCarousel || 'Annonces';

  const wrapper = document.createElement('div');
  wrapper.className = 'cyx-block cyx-block-property-carousel';
  wrapper.setAttribute('role', 'region');
  wrapper.setAttribute('aria-roledescription', 'carousel');
  wrapper.setAttribute('aria-label', sliderLabel);

  const trackContainer = document.createElement('div');
  trackContainer.className = 'cyx-block-pc-track-container';

  const track = document.createElement('div');
  track.className = 'cyx-block-pc-track';
  track.setAttribute('aria-live', 'polite');

  items.forEach((item, idx) => {
    track.appendChild(buildSlide(item, idx, items.length));
  });

  trackContainer.appendChild(track);
  wrapper.appendChild(trackContainer);

  // Between-properties navigation: a centered control bar (page arrows
  // around the dot indicators) below the card. Kept out of the image so it
  // never overlaps the per-photo arrows inside a card.
  const indicators = [];
  let prevBtn = null;
  let nextBtn = null;
  if (items.length > 1) {
    const controls = document.createElement('div');
    controls.className = 'cyx-block-pc-controls';

    prevBtn = makePageNav('prev', prevLabel);
    controls.appendChild(prevBtn);

    const ind = document.createElement('div');
    ind.className = 'cyx-block-pc-indicators';
    ind.setAttribute('role', 'tablist');
    items.forEach((_, idx) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cyx-block-pc-indicator' + (idx === 0 ? ' is-active' : '');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', `${idx + 1} / ${items.length}`);
      b.dataset.index = String(idx);
      ind.appendChild(b);
      indicators.push(b);
    });
    controls.appendChild(ind);

    nextBtn = makePageNav('next', nextLabel);
    controls.appendChild(nextBtn);

    wrapper.appendChild(controls);
  }

  // Carousel state machine
  let currentSlide = 0;
  const totalSlides = items.length;

  function update() {
    track.style.transform = `translateX(${-currentSlide * 100}%)`;
    indicators.forEach((el, i) => {
      const active = i === currentSlide;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (prevBtn) prevBtn.disabled = currentSlide === 0;
    if (nextBtn) nextBtn.disabled = currentSlide === totalSlides - 1;
  }

  function go(delta) {
    const next = Math.max(0, Math.min(totalSlides - 1, currentSlide + delta));
    if (next !== currentSlide) {
      currentSlide = next;
      update();
    }
  }

  if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); go(-1); });
  if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); go(1); });
  indicators.forEach((el, i) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      currentSlide = i;
      update();
    });
  });

  // Swipe on touch devices
  let touchStartX = 0;
  track.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });
  track.addEventListener('touchend', (e) => {
    const diff = touchStartX - e.changedTouches[0].screenX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) go(diff > 0 ? 1 : -1);
  }, { passive: true });

  update();
  return wrapper;
}

function makePageNav(direction, label) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `cyx-block-pc-pagenav cyx-block-pc-pagenav--${direction}`;
  b.setAttribute('aria-label', label);
  b.textContent = direction === 'prev' ? '‹' : '›';
  return b;
}

function buildSlide(item, index, total) {
  const slide = document.createElement('div');
  slide.className = 'cyx-block-pc-slide';
  slide.setAttribute('role', 'group');
  slide.setAttribute('aria-roledescription', 'slide');
  slide.setAttribute('aria-label', `${index + 1} / ${total}`);

  const card = document.createElement('div');
  card.className = 'cyx-block-pc-card';

  // Image area (mini carousel if several images, single image otherwise)
  const images = Array.isArray(item.images)
    ? item.images
        .map((src) => SecurityHelper.sanitizeURL(typeof src === 'string' ? src : (src && src.src) || ''))
        .filter(Boolean)
    : [];

  const imageArea = document.createElement('div');
  imageArea.className = 'cyx-block-pc-image-area';
  if (images.length === 0) {
    const placeholder = document.createElement('div');
    placeholder.className = 'cyx-block-pc-image-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.textContent = '🏠';
    imageArea.appendChild(placeholder);
  } else if (images.length === 1) {
    const img = document.createElement('img');
    img.src = images[0];
    img.alt = String(item.title || '');
    img.loading = 'lazy';
    img.className = 'cyx-block-pc-image';
    imageArea.appendChild(img);
  } else {
    imageArea.appendChild(buildMiniCarousel(images, String(item.title || '')));
  }
  card.appendChild(imageArea);

  // Text area
  const body = document.createElement('div');
  body.className = 'cyx-block-pc-body';

  if (item.title) {
    const title = document.createElement('div');
    title.className = 'cyx-block-pc-title';
    title.textContent = String(item.title);
    body.appendChild(title);
  }

  const metaParts = [];
  if (item.price) metaParts.push(String(item.price));
  if (item.surface) metaParts.push(String(item.surface));
  if (typeof item.rooms === 'number' || (typeof item.rooms === 'string' && item.rooms)) {
    metaParts.push(`${item.rooms} p.`);
  }
  if (item.location) metaParts.push(String(item.location));
  if (metaParts.length) {
    const meta = document.createElement('div');
    meta.className = 'cyx-block-pc-meta';
    meta.textContent = metaParts.join(' • ');
    body.appendChild(meta);
  }

  if (Array.isArray(item.features) && item.features.length) {
    const feats = document.createElement('ul');
    feats.className = 'cyx-block-pc-features';
    for (const f of item.features) {
      if (!f) continue;
      const li = document.createElement('li');
      li.textContent = String(f);
      feats.appendChild(li);
    }
    body.appendChild(feats);
  }

  const safeUrl = SecurityHelper.sanitizeURL(item.url || '');
  if (safeUrl) {
    const a = document.createElement('a');
    a.href = safeUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.className = 'cyx-block-pc-link';
    a.textContent = (item.linkText && String(item.linkText)) || 'Voir l’annonce';
    body.appendChild(a);
  }

  card.appendChild(body);
  slide.appendChild(card);
  return slide;
}

function buildMiniCarousel(images, alt) {
  const container = document.createElement('div');
  container.className = 'cyx-block-pc-mini';

  const strip = document.createElement('div');
  strip.className = 'cyx-block-pc-mini-strip';

  images.forEach((src) => {
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    img.loading = 'lazy';
    img.className = 'cyx-block-pc-mini-img';
    strip.appendChild(img);
  });

  container.appendChild(strip);

  // Photo counter badge ("1/N"), updated as the strip moves.
  const counter = document.createElement('div');
  counter.className = 'cyx-block-pc-mini-count';
  counter.setAttribute('aria-hidden', 'true');
  container.appendChild(counter);

  let current = 0;
  function update() {
    strip.style.transform = `translateX(${-current * 100}%)`;
    counter.textContent = `${current + 1}/${images.length}`;
  }

  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'cyx-block-pc-mini-nav cyx-block-pc-mini-nav--prev';
  prev.setAttribute('aria-label', 'Image précédente');
  prev.textContent = '‹';
  prev.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    current = (current - 1 + images.length) % images.length;
    update();
  });

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'cyx-block-pc-mini-nav cyx-block-pc-mini-nav--next';
  next.setAttribute('aria-label', 'Image suivante');
  next.textContent = '›';
  next.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    current = (current + 1) % images.length;
    update();
  });

  container.appendChild(prev);
  container.appendChild(next);

  update();
  return container;
}
